// [S-383/S-385] Draft Workspace hooks. Backed by the admin REST API
// (apps/admin/src/routes/drafts.ts):
//
//   GET    /drafts                                   → list (caller's tenant, readable)
//   POST   /drafts                                   → create
//   GET    /drafts/:id                               → fetch one
//   PATCH  /drafts/:id                               → update metadata
//   DELETE /drafts/:id                               → delete (owner)
//   POST   /drafts/:id/share                         → share with a tenant member
//   DELETE /drafts/:id/collaborators/:uid            → unshare
//   POST   /drafts/:id/versions                      → save a new version
//   GET    /drafts/:id/versions                      → version history (newest-first)
//   GET    /drafts/:id/versions/:n                   → one version
//   POST   /drafts/:id/versions/:n/restore           → restore as a new version
//   POST   /drafts/:id/versions/:n/review            → run a review on a version
//   GET    /drafts/:id/reviews                        → reviews (newest-first)
//   POST   /drafts/:id/reviews/:rid/findings/:fid/triage → apply/ignore a finding
//
// Access control (owner-edit, same-tenant share, read/write collaborators,
// tenant isolation) is enforced server-side by DraftService; these hooks are
// thin transport. Wire types mirror @csai/core's Draft/DraftVersion/DraftReview
// inline so the web bundle doesn't pull in the core barrel for types.
import { useCallback } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi, useApiBlob, useApiStream } from "./api";
import type {
  ReviewAgentEvent,
  ReviewDeviation,
  ReviewPrecedent,
  Review,
} from "./reviewsQuery";
// [S-714/S-715] Type-only — erased at build time, so this doesn't pull the
// core barrel's runtime code into the web bundle (chatQuery.ts does the same
// for RecordRef).
import type { DraftCitation } from "@csai/core";
export type { DraftCitation };

export type DealType = Review["dealType"];
export type GuildCode = Review["guild"];

export type DraftSharePermission = "read" | "write";

export type DraftCollaborator = {
  uid: string;
  permission: DraftSharePermission;
};

export type Draft = {
  id: string;
  tenantId: string;
  ownerUid: string;
  title: string;
  dealType: DealType;
  guild: GuildCode;
  /** [context-picker] Short form or long form, copied from the certified
   *  template the draft was generated from. null for pasted/imported drafts
   *  and everything created before it was recorded. */
  documentForm?: "deal_memo" | "long_form" | null;
  currentVersion: number;
  /** Character length of the latest saved version's content (denormalized
   *  by the backend). 0 when no version has been saved yet, or for
   *  pre-migration drafts until their next save. */
  contentLength: number;
  /** [S-798] Set while a chat-started generation is still running after the
   *  reply (`building`), then `complete` / `failed`; null for ordinary drafts. */
  build?: {
    status: "building" | "complete" | "failed";
    error: string | null;
    startedAt?: string;
    /** Where a `building` generation has got to (parts written of the total, a
     *  correction pass, the term check, the save). */
    progress?: { stage: "writing" | "correcting" | "checking" | "saving"; done?: number; total?: number };
    /** Agreed deal terms the landed draft does not carry — reported, not drafted. */
    unplacedTerms?: Array<{ name: string; value: string }>;
    /** Template wording the deal did not call for (removed) and wording that could not be confirmed (left in brackets). */
    optionalWording?: { omitted: string[]; unconfirmed: string[] };
    /** What the draft still leaves to do; filled/total only when the template's slots were filled. */
    whatsLeft?: { variablesFilled: number | null; variables: number | null; variablesLeft: number; notesLeft: number };
  } | null;
  sharedWith: DraftCollaborator[];
  createdAt: string;
  updatedAt: string;
};

export type DraftVersion = {
  draftId: string;
  version: number;
  content: string;
  /** [S-714] Per-value provenance for `content` — empty for a manual edit or
   *  generation with no traceable sources. */
  citations: DraftCitation[];
  authorUid: string;
  createdAt: string;
};

export type DraftFindingStatus = "open" | "apply" | "ignore";

export type DraftReviewFinding = ReviewDeviation & {
  status: DraftFindingStatus;
};

export type DraftReview = {
  id: string;
  draftId: string;
  version: number;
  templateId: string | null;
  deviations: DraftReviewFinding[];
  precedents: ReviewPrecedent[];
  reviewModel: string | null;
  runByUid: string;
  createdAt: string;
};

export const DRAFTS_KEY = ["drafts"] as const;

// ── List + detail ──────────────────────────────────────────────────────────

export function useDrafts(): UseQueryResult<Draft[], Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...DRAFTS_KEY, "list"] as const,
    queryFn: async () => {
      const res = await api<{ drafts: Draft[] }>("/drafts");
      // Never return undefined (React Query rejects it) — a malformed/empty
      // body degrades to an empty list rather than throwing in the cache.
      return res.drafts ?? [];
    },
  });
}

export function useDraft(draftId: string | undefined): UseQueryResult<Draft, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...DRAFTS_KEY, draftId] as const,
    enabled: draftId !== undefined && draftId.length > 0,
    queryFn: () => api<Draft>(`/drafts/${encodeURIComponent(draftId!)}`),
    // [S-798] A chat-started generation lands after the reply; poll until it does.
    refetchInterval: (query) => (query.state.data?.build?.status === "building" ? 2500 : false),
  });
}

// ── Mutations: metadata ──────────────────────────────────────────────────────

export type CreateDraftInput = {
  title: string;
  dealType: DealType;
  guild: GuildCode;
};

export function useCreateDraft(): UseMutationResult<Draft, Error, CreateDraftInput> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => api<Draft>("/drafts", { method: "POST", body: input }),
    onSuccess: (draft) => {
      qc.setQueryData([...DRAFTS_KEY, draft.id], draft);
      void qc.invalidateQueries({ queryKey: [...DRAFTS_KEY, "list"] });
    },
  });
}

// [S-387] Upload a contract file → server extracts text and classifies it
// against the taxonomy. NOTHING is persisted: the result is a *suggestion*
// the user reviews/edits before the draft is created (via useCreateDraft +
// useSaveVersion). `confidence`/`tier`/`anchorCBA` are null and `error` is
// set when classification was unavailable; the title/dealType/guild then
// carry safe defaults the user corrects.
export type DraftClassificationSuggestion = {
  title: string;
  dealType: DealType;
  guild: GuildCode;
  tier: string | null;
  anchorCBA: string | null;
  confidence: number | null;
  error?: string;
};

export type ClassifyDraftResult = {
  /** Extracted draft content as editor HTML — held client-side until the
   *  user confirms, then saved as v1. */
  content: string;
  filename: string;
  suggestion: DraftClassificationSuggestion;
};

export function useClassifyDraft(): UseMutationResult<
  ClassifyDraftResult,
  Error,
  { file: File }
> {
  const api = useApi("admin");
  return useMutation({
    mutationFn: ({ file }) => {
      const form = new FormData();
      form.append("file", file, file.name);
      return api<ClassifyDraftResult>("/drafts/classify", {
        method: "POST",
        body: form,
      });
    },
  });
}

export type UpdateDraftInput = {
  id: string;
  patch: { title?: string; dealType?: DealType; guild?: GuildCode };
};

export function useUpdateDraft(): UseMutationResult<Draft, Error, UpdateDraftInput> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }) =>
      api<Draft>(`/drafts/${encodeURIComponent(id)}`, { method: "PATCH", body: patch }),
    onSuccess: (draft) => {
      qc.setQueryData([...DRAFTS_KEY, draft.id], draft);
      void qc.invalidateQueries({ queryKey: [...DRAFTS_KEY, "list"] });
    },
  });
}

export function useDeleteDraft(): UseMutationResult<void, Error, string> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api<void>(`/drafts/${encodeURIComponent(id)}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...DRAFTS_KEY, "list"] });
    },
  });
}

/**
 * [S-635] Download a saved draft as DOCX. Hits POST /drafts/:id/export, then
 * synthesizes an <a download> click — same pattern as useExportTemplate. Throws
 * ApiError on 422 (empty draft) so the caller can surface it.
 */
export function useExportDraft(): UseMutationResult<{ filename: string }, Error, string> {
  const blobApi = useApiBlob("admin");
  return useMutation({
    mutationFn: async (draftId) => {
      const { blob, filename } = await blobApi(
        `/drafts/${encodeURIComponent(draftId)}/export`,
        { method: "POST" }
      );
      const finalName = filename ?? `${draftId}.docx`;
      const objectUrl = URL.createObjectURL(blob);
      try {
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = finalName;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      }
      return { filename: finalName };
    },
  });
}

// ── [S-633] Deal-memo → long-form promotion ──────────────────────────────────

export type PromoteToLongFormResult = {
  draftId: string;
  title: string;
  sourceDraftId: string;
  templateId: string;
};

/** Generate a long-form contract draft from a closed deal-memo draft. The new
 *  draft lands in the Drafts list; the caller navigates to it on success. Honest
 *  errors bubble as ApiError (422 no long-form template / empty source, 501 not
 *  configured, 502 generation failed) — never a fake draft. */
export function usePromoteDraftToLongForm(): UseMutationResult<
  PromoteToLongFormResult,
  Error,
  string
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dealMemoDraftId) =>
      api<PromoteToLongFormResult>(
        `/drafts/${encodeURIComponent(dealMemoDraftId)}/promote-to-long-form`,
        { method: "POST" }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...DRAFTS_KEY, "list"] });
    },
  });
}

// ── Sharing ──────────────────────────────────────────────────────────────────

export type ShareDraftInput = {
  draftId: string;
  uid: string;
  permission: DraftSharePermission;
};

export function useShareDraft(): UseMutationResult<Draft, Error, ShareDraftInput> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, uid, permission }) =>
      api<Draft>(`/drafts/${encodeURIComponent(draftId)}/share`, {
        method: "POST",
        body: { uid, permission },
      }),
    onSuccess: (draft) => qc.setQueryData([...DRAFTS_KEY, draft.id], draft),
  });
}

export function useUnshareDraft(): UseMutationResult<
  Draft,
  Error,
  { draftId: string; uid: string }
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, uid }) =>
      api<Draft>(
        `/drafts/${encodeURIComponent(draftId)}/collaborators/${encodeURIComponent(uid)}`,
        { method: "DELETE" }
      ),
    onSuccess: (draft) => qc.setQueryData([...DRAFTS_KEY, draft.id], draft),
  });
}

// ── Versions ──────────────────────────────────────────────────────────────────

export function useDraftVersions(
  draftId: string | undefined
): UseQueryResult<DraftVersion[], Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...DRAFTS_KEY, draftId, "versions"] as const,
    enabled: draftId !== undefined && draftId.length > 0,
    queryFn: async () => {
      const res = await api<{ versions: DraftVersion[] }>(
        `/drafts/${encodeURIComponent(draftId!)}/versions`
      );
      return res.versions;
    },
  });
}

export function useDraftVersion(
  draftId: string | undefined,
  version: number | undefined
): UseQueryResult<DraftVersion, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...DRAFTS_KEY, draftId, "versions", version] as const,
    enabled:
      draftId !== undefined &&
      draftId.length > 0 &&
      version !== undefined &&
      version > 0,
    queryFn: () =>
      api<DraftVersion>(
        `/drafts/${encodeURIComponent(draftId!)}/versions/${version}`
      ),
  });
}

export function useSaveVersion(): UseMutationResult<
  DraftVersion,
  Error,
  { draftId: string; content: string }
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, content }) =>
      api<DraftVersion>(`/drafts/${encodeURIComponent(draftId)}/versions`, {
        method: "POST",
        body: { content },
      }),
    onSuccess: (_v, { draftId }) => {
      void qc.invalidateQueries({ queryKey: [...DRAFTS_KEY, draftId] });
      void qc.invalidateQueries({ queryKey: [...DRAFTS_KEY, "list"] });
    },
  });
}

export function useRestoreVersion(): UseMutationResult<
  DraftVersion,
  Error,
  { draftId: string; version: number }
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, version }) =>
      api<DraftVersion>(
        `/drafts/${encodeURIComponent(draftId)}/versions/${version}/restore`,
        { method: "POST" }
      ),
    onSuccess: (_v, { draftId }) => {
      void qc.invalidateQueries({ queryKey: [...DRAFTS_KEY, draftId] });
    },
  });
}

// ── Reviews + triage ─────────────────────────────────────────────────────────

export function useDraftReviews(
  draftId: string | undefined
): UseQueryResult<DraftReview[], Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...DRAFTS_KEY, draftId, "reviews"] as const,
    enabled: draftId !== undefined && draftId.length > 0,
    queryFn: async () => {
      const res = await api<{ reviews: DraftReview[] }>(
        `/drafts/${encodeURIComponent(draftId!)}/reviews`
      );
      return res.reviews;
    },
  });
}

export function useReviewVersion(): UseMutationResult<
  DraftReview,
  Error,
  { draftId: string; version: number }
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, version }) =>
      api<DraftReview>(
        `/drafts/${encodeURIComponent(draftId)}/versions/${version}/review`,
        { method: "POST" }
      ),
    onSuccess: (_r, { draftId }) => {
      void qc.invalidateQueries({ queryKey: [...DRAFTS_KEY, draftId, "reviews"] });
    },
  });
}

// [S-389] Streaming review of a draft version — POSTs to .../review/stream and
// dispatches each NDJSON line (progress event, final DraftReview, or error) to
// the handlers so the UI can show live progress (mirrors useRunReviewStream).
export type ReviewVersionStreamHandlers = {
  onEvent: (event: ReviewAgentEvent) => void;
  onReview: (review: DraftReview) => void;
  onError: (error: { code?: string; message?: string }) => void;
};

export function useReviewVersionStream(): (
  input: { draftId: string; version: number },
  handlers: ReviewVersionStreamHandlers
) => Promise<void> {
  const stream = useApiStream("admin");
  const qc = useQueryClient();
  return useCallback(
    async ({ draftId, version }, handlers) => {
      await stream(
        `/drafts/${encodeURIComponent(draftId)}/versions/${version}/review/stream`,
        { method: "POST" },
        (line) => {
          let obj: Record<string, unknown>;
          try {
            obj = JSON.parse(line) as Record<string, unknown>;
          } catch {
            return;
          }
          if (obj["event"] !== undefined) {
            handlers.onEvent(obj["event"] as ReviewAgentEvent);
          } else if (obj["draftReview"] !== undefined) {
            const review = obj["draftReview"] as DraftReview;
            void qc.invalidateQueries({
              queryKey: [...DRAFTS_KEY, draftId, "reviews"],
            });
            handlers.onReview(review);
          } else if (obj["error"] !== undefined) {
            handlers.onError(
              obj["error"] as { code?: string; message?: string }
            );
          }
        }
      );
    },
    [stream, qc]
  );
}

export function useTriageFinding(): UseMutationResult<
  DraftReview,
  Error,
  { draftId: string; reviewId: string; findingId: string; status: DraftFindingStatus }
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, reviewId, findingId, status }) =>
      api<DraftReview>(
        `/drafts/${encodeURIComponent(draftId)}/reviews/${encodeURIComponent(
          reviewId
        )}/findings/${encodeURIComponent(findingId)}/triage`,
        { method: "POST", body: { status } }
      ),
    onSuccess: (_r, { draftId }) => {
      void qc.invalidateQueries({ queryKey: [...DRAFTS_KEY, draftId, "reviews"] });
    },
  });
}
